import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getApiUrl } from "../../config/api";

const WorkingCart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const userId = "68ded148cbfe892dc87f3c45"; // Hardcoded for testing

  console.log("🛒 WORKING CART: Component rendering");

  useEffect(() => {
    console.log("🛒 WORKING CART: useEffect triggered - loading cart");
    loadCart();
  }, []);

  const loadCart = async () => {
    console.log("🛒 WORKING CART: Starting cart load");
    setLoading(true);
    setError(null);

    try {
      const apiUrl = getApiUrl(`/cart/${userId}`);
      console.log("🛒 WORKING CART: Making API call to:", apiUrl);

      const response = await fetch(apiUrl);
      console.log("🛒 WORKING CART: Response status:", response.status);

      if (response.ok) {
        const data = await response.json();
        console.log("🛒 WORKING CART: API response:", data);

        if (data?.items && data.items.length > 0) {
          const items = data.items.map((item) => ({
            _id: item.product._id,
            name: item.product.name,
            quantity: item.quantity,
            price: item.product.price,
            realTimeTotalPrice: item.realTimeTotalPrice,
            coverImage: item.product.coverImage,
            ...item.product,
          }));
          console.log("🛒 WORKING CART: Setting cart items:", items);
          setCartItems(items);
        } else {
          console.log("🛒 WORKING CART: No items in cart");
          setCartItems([]);
        }
      } else {
        const errorText = await response.text();
        console.error("🛒 WORKING CART: API error:", response.status, errorText);
        setError(`Failed to load cart: ${response.status}`);
      }
    } catch (err) {
      console.error("🛒 WORKING CART: Network error:", err);
      setError(`Network Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = async (productId, increment) => {
    console.log(`🛒 WORKING CART: Changing quantity for ${productId} by ${increment}`);
    setLoading(true);
    
    try {
      const endpoint = increment > 0 ? 'add-to-cart' : 'remove-from-cart';
      const method = increment > 0 ? 'POST' : 'DELETE';
      const body = { userId, productId, quantity: Math.abs(increment) };

      const response = await fetch(getApiUrl(`/cart/${endpoint}`), {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      if (response.ok) {
        console.log("🛒 WORKING CART: Quantity updated, reloading cart...");
        await loadCart();
      } else {
        const errorText = await response.text();
        console.error("🛒 WORKING CART: Error updating quantity:", response.status, errorText);
        setError(`Failed to update quantity: ${response.status}`);
      }
    } catch (error) {
      console.error("🛒 WORKING CART: Network error updating quantity:", error);
      setError(`Network Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveItem = async (productId) => {
    console.log(`🛒 WORKING CART: Removing item ${productId}`);
    setLoading(true);
    
    try {
      const response = await fetch(getApiUrl('/cart/remove-from-cart'), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, productId, quantity: 0 }),
      });

      if (response.ok) {
        console.log("🛒 WORKING CART: Item removed, reloading cart...");
        await loadCart();
      } else {
        const errorText = await response.text();
        console.error("🛒 WORKING CART: Error removing item:", response.status, errorText);
        setError(`Failed to remove item: ${response.status}`);
      }
    } catch (error) {
      console.error("🛒 WORKING CART: Network error removing item:", error);
      setError(`Network Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getSubtotal = () => {
    return cartItems.reduce((total, item) => total + (Number(item.realTimeTotalPrice) || 0), 0);
  };

  const calculateTotals = () => {
    const subtotal = getSubtotal();
    const tax = Math.round(subtotal * 0.03);
    const shipping = subtotal > 5000 ? 0 : 50;
    const discount = 0;
    const total = subtotal + tax + shipping - discount;

    return { subtotal, tax, shipping, discount, total };
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-xl font-bold text-gray-700">Loading your cart...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4 text-xl">Error: {error}</p>
          <button
            onClick={loadCart}
            className="px-6 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition duration-300"
          >
            Retry Loading Cart
          </button>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center">
        <div className="text-6xl mb-6">🛒</div>
        <h2 className="text-3xl font-bold mb-3 text-gray-800">Your cart is empty!</h2>
        <p className="text-lg text-gray-600 mb-6">
          Looks like you haven't added anything to your cart yet.
        </p>
        <Link to="/productlist">
          <button className="bg-orange-500 text-white px-8 py-3 text-lg font-semibold rounded-md hover:bg-orange-600 transition-all shadow-md">
            Start Shopping
          </button>
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-[#FAF7F2] min-h-screen px-4 sm:px-6 lg:px-10 py-10 space-y-10">
      {/* Success Header */}
      <div className="mb-4 p-4 bg-green-500 text-white rounded-lg shadow-md">
        <h1 className="text-2xl font-bold">✅ WORKING CART COMPONENT</h1>
        <p>Cart loaded successfully with {cartItems.length} items</p>
        <p>Loaded at: {new Date().toLocaleTimeString()}</p>
      </div>

      {/* Cart Table */}
      <div className="p-6 bg-white shadow-md w-full rounded-lg">
        <h1 className="text-2xl font-bold mb-6 text-gray-800">Shopping Cart</h1>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-[#784421] text-white">
              <tr>
                <th className="py-3 px-4 rounded-tl-lg">Product</th>
                <th className="py-3 px-4">Price</th>
                <th className="py-3 px-4">Quantity</th>
                <th className="py-3 px-4">Subtotal</th>
                <th className="py-3 px-4 rounded-tr-lg">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cartItems.map((item) => (
                <tr key={item._id} className="border-b border-gray-200 last:border-b-0">
                  <td className="py-4 px-4 flex items-center space-x-4">
                    <img
                      src={item.coverImage || "https://via.placeholder.com/50"}
                      alt={item.name}
                      className="w-20 h-20 object-cover rounded-md shadow-sm"
                    />
                    <span className="truncate font-semibold text-gray-700">{item.name}</span>
                  </td>
                  <td className="py-4 px-4 text-gray-600">
                    ₹{(Number(item.realTimeTotalPrice || 0) / item.quantity).toFixed(2)}
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex items-center border border-gray-300 w-fit rounded-full overflow-hidden shadow-sm">
                      <button
                        onClick={() => handleQuantityChange(item._id, -1)}
                        className="px-3 py-1 bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors duration-200"
                        disabled={loading}
                      >
                        -
                      </button>
                      <span className="px-4 text-lg font-medium text-gray-800">{item.quantity}</span>
                      <button
                        onClick={() => handleQuantityChange(item._id, 1)}
                        className="px-3 py-1 bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors duration-200"
                        disabled={loading}
                      >
                        +
                      </button>
                    </div>
                  </td>
                  <td className="py-4 px-4 font-semibold text-gray-800">₹{Number(item.realTimeTotalPrice || 0).toFixed(2)}</td>
                  <td className="py-4 px-4">
                    <button
                      onClick={() => handleRemoveItem(item._id)}
                      className="text-red-600 hover:text-red-800 transition-colors duration-200 flex items-center space-x-1"
                      disabled={loading}
                    >
                      <span className="text-xl">🗑️</span>
                      <span>Remove</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex justify-between items-center">
          <Link to="/productlist">
            <button className="px-6 py-3 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition duration-300 flex items-center space-x-2">
              <span className="text-xl">🛍️</span>
              <span>Continue Shopping</span>
            </button>
          </Link>
          <button
            onClick={loadCart}
            className="px-6 py-3 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition duration-300 flex items-center space-x-2"
            disabled={loading}
          >
            <span className="text-xl">🔄</span>
            <span>Update Cart</span>
          </button>
        </div>
      </div>

      {/* Order Summary */}
      <div className="flex justify-end">
        <div className="w-full sm:w-1/2 lg:w-1/3 bg-white p-6 shadow-md rounded-lg">
          <h2 className="text-xl font-bold mb-4 text-gray-800">Order Summary</h2>
          <div className="space-y-3 text-gray-700">
            <div className="flex justify-between">
              <span>Subtotal:</span>
              <span>₹{totals.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Tax (3%):</span>
              <span>₹{totals.tax.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span>Shipping:</span>
              <span>₹{totals.shipping.toFixed(2)}</span>
            </div>
            <div className="flex justify-between font-bold text-lg border-t border-gray-300 pt-3 mt-3 text-gray-900">
              <span>Total:</span>
              <span>₹{totals.total.toFixed(2)}</span>
            </div>
          </div>
          <Link
            to="/checkout"
            className="mt-6 block w-full text-center bg-yellow-500 text-white py-3 rounded-md hover:bg-yellow-600 transition duration-300 text-lg font-semibold flex items-center justify-center space-x-2"
          >
            <span className="text-xl">💳</span>
            <span>Proceed to Checkout</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default WorkingCart;
